import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const values = [
  {
    icon: '☕',
    title: 'Premium Quality',
    description: 'We source the finest ingredients and craft every drink and dish with love and precision.',
  },
  {
    icon: '🌿',
    title: 'Fresh & Healthy',
    description: 'From detox waters to smoothie bowls, we offer wholesome choices for the health-conscious.',
  },
  {
    icon: '🤝',
    title: 'Community First',
    description: 'We\'re more than a café — we\'re a gathering place for friends, families, and neighbors.',
  },
  {
    icon: '✨',
    title: 'Modern Vibes',
    description: 'Trendy shakes, cool interiors, and an Instagram-worthy ambiance that keeps you coming back.',
  },
];

const timeline = [
  { year: '2024', event: 'Squeeze N Sip was born near Kartigi Bus Stop' },
  { year: '2024', event: 'Launched our Classic, Trendy & Healthy menus' },
  { year: '2025', event: 'Became the neighborhood\'s favorite hangout spot' },
  { year: '2026', event: 'Continuing to innovate with new flavors and experiences' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

export default function About() {
  return (
    <div className="bg-cream-50">
      {/* Hero Banner */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/about.png"
            alt="Squeeze N Sip Café Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-espresso-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-espresso-950/90 to-transparent" />
        </div>
        <div className="relative z-10 text-center px-4">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="category-badge mb-6 inline-flex bg-white/10 border-white/20 text-white/80"
          >
            <span>📖</span> Our Story
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            About <span className="text-gradient-gold">Us</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-white/70 text-lg max-w-lg mx-auto"
          >
            The story behind your favorite cup of happiness
          </motion.p>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/hero.png"
                  alt="Our Café"
                  className="w-full h-[450px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso-950/30 to-transparent" />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6 text-center">
                <div
                  className="text-4xl font-bold text-gradient-gold"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  EST
                </div>
                <div className="text-2xl font-bold text-espresso-900">2024</div>
              </div>
              {/* Decorative */}
              <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-amber-400/30 rounded-2xl -z-10" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeUp}>
                <div className="divider-gold mb-6" />
              </motion.div>
              <motion.h2
                variants={fadeUp}
                custom={1}
                className="text-3xl md:text-4xl font-bold text-espresso-900 mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                A Passion for <span className="text-gradient-gold">Great Coffee</span>
                <br />& Memorable Moments
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-espresso-500 leading-relaxed mb-4">
                Squeeze N Sip was born from a simple idea — to create a warm, welcoming space 
                where everyone feels at home. Located near Kartigi Bus Stop, our café has been 
                a neighborhood favorite since 2024.
              </motion.p>
              <motion.p variants={fadeUp} custom={3} className="text-espresso-500 leading-relaxed mb-6">
                We believe that a great café is more than just good coffee. It's about the atmosphere, 
                the people, and the little touches that make every visit special. From our handcrafted 
                espressos to our trending Oreo Shakes, every item on our menu is prepared with 
                care and passion.
              </motion.p>
              <motion.div variants={fadeUp} custom={4}>
                <Link to="/services" className="btn-primary">
                  Explore Our Menu
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeUp} className="category-badge mb-4 inline-flex">
              <span>💎</span> What We Stand For
            </motion.span>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="text-3xl md:text-4xl font-bold text-espresso-900 mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Our <span className="text-gradient-gold">Values</span>
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className="glass-card p-8 text-center group hover:shadow-xl transition-all duration-500"
              >
                <div className="text-4xl mb-4 group-hover:scale-125 transition-transform duration-500">
                  {value.icon}
                </div>
                <h3
                  className="text-lg font-bold text-espresso-900 mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {value.title}
                </h3>
                <p className="text-espresso-400 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="section-padding bg-cream-50">
        <div className="container-custom max-w-3xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeUp} className="category-badge mb-4 inline-flex">
              <span>📅</span> Our Journey
            </motion.span>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="text-3xl md:text-4xl font-bold text-espresso-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              The <span className="text-gradient-gold">Timeline</span>
            </motion.h2>
          </motion.div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-amber-400/30 -translate-x-1/2" />

            {timeline.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className={`relative flex items-center gap-6 mb-12 ${
                  idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Dot */}
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-amber-400 border-4 border-cream-50 z-10" />

                {/* Content */}
                <div className={`ml-16 md:ml-0 md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <div className="glass-card p-6">
                    <span className="text-amber-500 font-bold text-sm">{item.year}</span>
                    <p className="text-espresso-700 mt-1">{item.event}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Hours */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-warm-gradient" />
        <div className="absolute inset-0 opacity-10">
          <img src="/images/about.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeUp}
              className="text-4xl md:text-5xl font-bold text-white mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Visit Us <span className="text-gradient-gold">Anytime</span>
            </motion.h2>
            <motion.div variants={fadeUp} custom={1} className="max-w-md mx-auto">
              <div className="glass-card bg-white/10 backdrop-blur-xl border-white/10 p-8">
                <div className="text-5xl mb-4">🕐</div>
                <h3
                  className="text-2xl font-bold text-white mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Business Hours
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-white/80">
                    <span>Monday – Friday</span>
                    <span className="font-semibold text-amber-400">8 AM – 10 PM</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="flex justify-between items-center text-white/80">
                    <span>Saturday</span>
                    <span className="font-semibold text-amber-400">8 AM – 10 PM</span>
                  </div>
                  <div className="h-px bg-white/10" />
                  <div className="flex justify-between items-center text-white/80">
                    <span>Sunday</span>
                    <span className="font-semibold text-amber-400">8 AM – 10 PM</span>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} custom={2} className="mt-8">
              <Link to="/contact" className="btn-primary">
                📍 Get Directions
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
